import { contentRequest, office, type ContentFile } from '../../../packages/content-client/src'
import type {EditorPort} from './editor/EditorPort'
import type {EditorSelection} from './editor/EditorPort'
export type {EditorSelection} from './editor/EditorPort'

export interface Evidence { origin?:string; memory_id?:string; file_id:string; version:number; section_id:string; content_hash:string; start?:number; end?:number }
function withdrawn(message:string){return Object.assign(Error(message),{invalidatesReview:true})}
async function authorized<T>(request:Promise<T>):Promise<T>{
 try{return await request}catch(error){if([401,403,404,409].includes((error as {status?:number}).status))throw withdrawn(error instanceof Error?error.message:'Source access changed.');throw error}
}
export interface ToolsBridge {
 selectionFirst?:boolean
 imageInsertion?:boolean
 idle:()=>boolean
 capture:()=>Promise<EditorSelection>
 prepare:(selection?:EditorSelection)=>Promise<{file:ContentFile;selection:EditorSelection}>
 refresh:()=>Promise<ContentFile>
 insert:(result:{version:number;memory_context?:{file_ids:string[];token:string};citations:Evidence[];data_source?:ContentFile|null;context_guard?:{file_id:string;token:string}},data:string|{bytes:Uint8Array;pixelsHash:string},selection:EditorSelection)=>Promise<void>
 dispose:()=>void
 cancel?:()=>void
 invalidateEvidence?:()=>void
}

export function createContextTools(file:ContentFile,editor:EditorPort,indexed:()=>void):ToolsBridge {
 let stopped=false
 const current=()=>!stopped
 const capture=()=>editor.run('tools','selection',tx=>tx.captureSelection(),current)
 return {
  get imageInsertion(){return editor.state().capabilities?.imagePaste===true},
  idle:editor.idle,
  capture,
  async refresh(){
   for(let i=0;i<30;i++){
    if(stopped)throw Error('The editor was closed.')
    const latest=await office<ContentFile>('/files/'+file.file_id)
    try{const graph=await contentRequest<{version:number}>(`/knowledge/office/files/${file.file_id}/graph`);if(graph.version===latest.version)return latest}catch{}
    await new Promise<void>(resolve=>setTimeout(resolve,700))
   }
   throw Error('The latest saved revision is still being indexed. Retry shortly.')
  },
  async prepare(chosen){return editor.run('tools','prepare',async tx=>{
   const before=await office<ContentFile>('/files/'+file.file_id)
   if(chosen)await tx.focus(chosen)
   const selection=chosen||await tx.captureSelection()
   const guarded=editor.state().capabilities?.guardedSelection===true
   if(!file.can_write&&selection.modified&&guarded)throw Error('Save a private copy before asking about local changes to a view-only file.')
   if(guarded&&!file.can_write&&(before.version!==file.version||before.content_hash!==file.content_hash))throw Error('The saved file changed. Reopen its current revision before generating.')
   const saved=file.can_write?await tx.save():null
   for(let i=0;i<30;i++){
    if(stopped||!tx.current())throw Error('The editor request was cancelled.')
    const latest=await office<ContentFile>('/files/'+file.file_id)
    if(guarded&&saved&&(latest.version!==saved.version||latest.content_hash!==saved.content_hash))throw Error('The saved file changed while preparing. Generate a fresh preview.')
    const graph=await contentRequest<{version:number}>(`/knowledge/office/files/${file.file_id}/graph`).catch(()=>null)
     if(graph?.version===latest.version&&(!selection.modified||latest.version>before.version||guarded)){
      // ArtCraft save verifies exact snapshot bytes and acknowledges only its
      // current model. Reject typing/selection changes during that save too.
      if(guarded)await tx.focus(selection)
      indexed();return {file:latest,selection}
     }
    await new Promise<void>(resolve=>setTimeout(resolve,700))
   }
   throw Error('The saved file is still being indexed. Retry shortly.')
  },current)},
  async insert(result,data,selection){return editor.run('tools','insert',async tx=>{
   if(stopped||!file.can_write)throw Error('Editing access is required.')
   const target=await authorized(office<ContentFile>('/files/'+file.file_id))
   if(!target.can_write||target.version!==result.version)throw withdrawn('The target changed or access was removed. Generate a fresh preview.')
   async function contextGuard(){if(result.context_guard){const value=await authorized(contentRequest<{token:string}>('/knowledge/office/files/'+encodeURIComponent(file.file_id)+'/suggestions/context'));if(value.token!==result.context_guard.token)throw withdrawn('Project sources or human updates changed. Review a fresh suggestion.')}}
   await contextGuard()
   if(result.memory_context){
    const snapshot=await authorized(contentRequest<{snapshot_token:string}>('/knowledge/office/memory/context?file_id='+encodeURIComponent(file.file_id)))
    if(snapshot.snapshot_token!==result.memory_context.token)throw withdrawn('Human knowledge changed. Generate a fresh preview.')
   }
   for(const ref of result.citations){
    const value=await authorized(contentRequest<{current_version:number;content_hash:string}>(ref.origin==='human'?`/knowledge/office/memory/${ref.memory_id}/validate?revision=${ref.version}`:`/knowledge/office/files/${ref.file_id}/revisions/${ref.version}/sections/${ref.section_id}?start=${ref.start||0}${ref.end?'&end='+ref.end:''}`))
    if(value.current_version!==ref.version||value.content_hash!==ref.content_hash)throw withdrawn('A source changed. Generate a fresh preview.')
   }
   if(result.data_source){
    const source=await authorized(office<ContentFile>('/files/'+result.data_source.file_id))
    if(source.version!==result.data_source.version||source.content_hash!==result.data_source.content_hash)throw withdrawn('The chart data changed. Generate a fresh chart.')
   }
   if(stopped)throw Error('The editor was closed.')
   const finalTarget=await authorized(office<ContentFile>('/files/'+file.file_id))
   if(!finalTarget.can_write||finalTarget.version!==target.version||finalTarget.content_hash!==target.content_hash)throw withdrawn('The target changed or access was removed. Generate a fresh preview.')
   await contextGuard()
   await tx.focus(selection)
   await tx.paste(typeof data==='string'?data:data.bytes)
   if(typeof data!=='string'){
    // A native save response can precede durable storage. Do not close the frame
    // or report success until the exact rendered pixels occur in Forge bytes.
    await tx.save()
    for(let i=0;i<35;i++){
     if(stopped)throw Error('The editor was closed before chart persistence was confirmed.')
     const saved=await contentRequest<{present:boolean;version:number}>(`/knowledge/office/files/${file.file_id}/analysis/verify-image`,{method:'POST',body:JSON.stringify({pixels_sha256:data.pixelsHash})})
     if(saved.present&&saved.version>result.version){
      const graph=await contentRequest<{version:number}>(`/knowledge/office/files/${file.file_id}/graph`).catch(()=>null)
      if(graph?.version===saved.version){indexed();return}
     }
     await new Promise<void>(resolve=>setTimeout(resolve,700))
    }
    throw Error('The chart is visible but its Forge save could not be confirmed. Keep the editor open and retry its save.')
   }
  },current)},
  cancel(){editor.cancel('tools')},
  invalidateEvidence(){indexed()},
  dispose(){stopped=true;editor.cancel('tools')}
 }
}
